import React, { useState, useEffect } from 'react';
import { useAuth } from '../components/AuthContext';
import { useNavigate } from 'react-router-dom';
import { fetchContractsEndingSoon } from '../api/stats';
import {
    fetchScheduledNotifications,
    createScheduledNotification,
    deleteScheduledNotification,
    type NotificationFrequency,
    type NotificationMethod
} from '../api/alerts';

interface Contrat {
  id: number;
  contract_id?: number | null;
    employee_pk?: number | null;
  nom: string;
  poste: string;
  departement: string;
  dateDebut: string;
  dateFin: string;
  email?: string;
  photo?: string;
}

interface Rappel {
  id: number;
  contratIds: number[];
  dateRappel: string;
  heure: string;
  frequence: 'ponctuel' | 'quotidien' | 'hebdomadaire';
  methode: ('email' | 'notification')[];
  message: string;
  statut: 'actif' | 'termine';
}

// Mapper pour convertir les types de fréquence
const mapFrequencyToApi = (freq: 'ponctuel' | 'quotidien' | 'hebdomadaire'): NotificationFrequency => {
    switch (freq) {
        case 'ponctuel': return 'ONCE';
        case 'quotidien': return 'DAILY';
        case 'hebdomadaire': return 'WEEKLY';
        default: return 'ONCE';
    }
};

const mapFrequencyFromApi = (freq: string): 'ponctuel' | 'quotidien' | 'hebdomadaire' => {
    switch (freq) {
        case 'ONCE': return 'ponctuel';
        case 'DAILY': return 'quotidien';
        case 'WEEKLY': return 'hebdomadaire';
        default: return 'ponctuel';
    }
};

const mapNotificationMethod = (methode: ('email' | 'notification')[]): NotificationMethod => {
    if (methode.includes('email') && methode.includes('notification')) return 'BOTH';
    if (methode.includes('email')) return 'EMAIL';
    return 'IN_APP';
};

// Données récupérées depuis l'API
// (plus besoin de finContratData, les données sont chargées via useEffect)

const FinContrat: React.FC = () => {
    const { user } = useAuth();
    const navigate = useNavigate();

    // États pour la gestion des rappels
    const [contratsSelectionnes, setContratsSelectionnes] = useState<number[]>([]);
    const [modalRappelOpen, setModalRappelOpen] = useState(false);
    const [rappelPlanifies, setRappelPlanifies] = useState<Rappel[]>([]);
    const [nouveauRappel, setNouveauRappel] = useState<Omit<Rappel, 'id' | 'statut'>>({
        contratIds: [],
        dateRappel: new Date().toISOString().split('T')[0],
        heure: '09:00',
        frequence: 'ponctuel',
        methode: ['notification'],
        message: 'Rappel automatique : fin de contrat approchant'
    });

    // État des données et chargement API
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [rows, setRows] = useState<Contrat[]>([]);

    // Charger les rappels planifiés
    const loadRappels = async () => {
        try {
            const notifications = await fetchScheduledNotifications({ status: 'ACTIVE' });
            const mapped: Rappel[] = notifications.map(n => ({
                id: n.id,
                contratIds: [], // Pas utilisé dans l'affichage
                dateRappel: n.scheduled_date,
                heure: n.scheduled_time,
                frequence: mapFrequencyFromApi(n.frequency),
                methode: ['notification'], // Simplifié pour l'affichage
                message: n.title,
                statut: n.status === 'ACTIVE' ? 'actif' : 'termine'
            }));
            setRappelPlanifies(mapped);
        } catch (err: any) {
            console.error('Erreur chargement rappels:', err);
        }
    };

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const toAbsolute = (url?: string | null) => {
                    if (!url) return '';
                    if (/^https?:\/\//i.test(url)) return url;
                    const apiBase = import.meta.env.VITE_API_URL?.replace(/\/api\/?$/, '') || '';
                    return `${apiBase}${url.startsWith('/') ? url : `/${url}`}`;
                };

                const list = await fetchContractsEndingSoon();
                if (!mounted) return;
                // Adapter au type local Contrat
                const mapped: Contrat[] = list.map(item => ({
                    id: item.id,
                    contract_id: item.contract_id,
                    employee_pk: item.id ?? null,
                    nom: item.employee_name,
                    poste: item.position_display || '',
                    departement: item.department_name || '—',
                    dateDebut: item.contract_start_date || '',
                    dateFin: item.contract_end_date,
                    email: item.email || '',
                    photo: toAbsolute(item.photo)
                }));
                setRows(mapped);

                // Charger les rappels planifiés
                await loadRappels();
            } catch (e: any) {
                if (!mounted) return;
                setError(e?.message || 'Erreur lors du chargement des contrats');
                setRows([]);
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => { mounted = false; };
    }, []);

    // Protection de la route
    if (!user) {
        navigate('/login', { replace: true });
        return null;
    }

    // Calculer les jours restants
    const calculerJoursRestants = (dateFin: string): number => {
        const aujourdhui = new Date();
        const dateFinContrat = new Date(dateFin);
        const difference = dateFinContrat.getTime() - aujourdhui.getTime();
        return Math.ceil(difference / (1000 * 3600 * 24));
    };

    // Formater la date en français
    const formaterDate = (date: string): string => {
        // Une date absente ou illisible ne doit pas produire « Invalid Date »
        // dans le tableau ni dans le fichier exporté.
        if (!date) return '';
        const valeur = new Date(date);
        if (Number.isNaN(valeur.getTime())) return '';
        return valeur.toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    // Gestion de la sélection des contrats
    const toggleSelectionContrat = (contratId: number) => {
        setContratsSelectionnes(prev => 
            prev.includes(contratId) 
                ? prev.filter(id => id !== contratId)
                : [...prev, contratId]
        );
    };

    // Ouvrir le modal de planification
    const ouvrirModalRappel = () => {
        if (contratsSelectionnes.length === 0) {
            alert('Veuillez sélectionner au moins un employé');
            return;
        }

        // Récupérer les contract_ids des contrats sélectionnés (s'ils existent)
        // Mais le rappel est basé sur les dates de fin, pas sur les contrats
        const contractIds = contratsSelectionnes
            .map(id => {
                const contrat = rows.find(c => c.id === id);
                return contrat?.contract_id;
            })
            .filter((contractId): contractId is number => contractId != null);

        // Créer un message avec les noms et dates de fin
        const employesInfo = contratsSelectionnes
            .map(id => {
                const contrat = rows.find(c => c.id === id);
                if (!contrat) return null;
                return `${contrat.nom} (fin le ${formaterDate(contrat.dateFin)})`;
            })
            .filter(Boolean)
            .join(', ');

        const messageDefaut = `Rappel : Fin de contrat pour ${employesInfo}`;

        setNouveauRappel(prev => ({
            ...prev,
            contratIds: contractIds, // Peut être vide si aucun contract_id
            message: messageDefaut
        }));
        setModalRappelOpen(true);
    };

    // Planifier un nouveau rappel
    const planifierRappel = async () => {
        try {
            // Préparer les données - envoyer contract_ids seulement s'il y en a
            const notificationData: any = {
                scheduled_date: nouveauRappel.dateRappel,
                scheduled_time: nouveauRappel.heure,
                frequency: mapFrequencyToApi(nouveauRappel.frequence),
                title: `Rappel fin de contrat - ${contratsSelectionnes.length} employé(s)`,
                message: nouveauRappel.message,
                notification_method: mapNotificationMethod(nouveauRappel.methode)
            };

            // Ajouter contract_ids seulement s'il y en a
            if (nouveauRappel.contratIds.length > 0) {
                notificationData.contract_ids = nouveauRappel.contratIds;
            }

            // Créer la notification programmée via l'API
            await createScheduledNotification(notificationData);

            // Recharger les rappels
            await loadRappels();

            setModalRappelOpen(false);
            setContratsSelectionnes([]);

            alert(`Rappel planifié avec succès pour ${contratsSelectionnes.length} employé(s)`);
        } catch (err: any) {
            console.error('Erreur planification rappel:', err);
            const errorMsg = err?.response?.data?.contract_ids?.[0] || err?.message || 'Erreur lors de la planification du rappel';
            alert(`Erreur: ${errorMsg}`);
        }
    };

    // Supprimer un rappel
    const supprimerRappel = async (rappelId: number) => {
        try {
            await deleteScheduledNotification(rappelId);
            setRappelPlanifies(prev => prev.filter(r => r.id !== rappelId));
        } catch (err: any) {
            console.error('Erreur suppression rappel:', err);
            alert('Erreur lors de la suppression du rappel');
        }
    };

    // Obtenir le nom des contrats sélectionnés
    const getNomsContratsSelectionnes = () => {
        return contratsSelectionnes.map(id =>
            rows.find((c: Contrat) => c.id === id)?.nom
        ).filter(Boolean).join(', ');
    };

    // Exporter la liste des contrats en CSV
    const exporterListeCSV = () => {
        if (rows.length === 0) {
            alert('Aucun contrat à exporter');
            return;
        }

        // Mêmes colonnes que le tableau affiché, dans le même ordre : un export
        // qui omet des colonnes visibles à l'écran passe pour un export tronqué.
        const headers = [
            'Nom', 'Poste', 'Département', 'Date de début', 'Date de fin',
            'Jours restants', 'Email',
        ];
        // Les valeurs peuvent contenir le séparateur : on les protège.
        const echapper = (valeur: string) =>
            /[;"\n]/.test(valeur) ? `"${valeur.replace(/"/g, '""')}"` : valeur;

        const csvContent = [
            headers.join(';'),
            ...rows.map(contrat => {
                const joursRestants = calculerJoursRestants(contrat.dateFin);
                return [
                    contrat.nom,
                    contrat.poste,
                    contrat.departement || '',
                    formaterDate(contrat.dateDebut),
                    formaterDate(contrat.dateFin),
                    joursRestants.toString(),
                    contrat.email || '',
                ].map(v => echapper(String(v ?? ''))).join(';');
            })
        ].join('\n');

        // Ajouter le BOM UTF-8 pour Excel
        const BOM = '\uFEFF';
        const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);

        // Télécharger le fichier
        const link = document.createElement('a');
        link.href = url;
        link.download = `fins_de_contrat_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    return (
        <div className="min-h-screen bg-base-200 p-6">
            {/* Header avec bouton retour */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                    <button 
                        onClick={() => navigate('/dashboard')}
                        className="btn btn-ghost btn-circle"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                    </button>
                    <div>
                        <h1 className="text-3xl font-bold">Fins de Contrat</h1>
                        <p className="text-base-content/70">Liste des contrats arrivant à échéance</p>
                    </div>
                </div>
                <button 
                    className="btn btn-error gap-2"
                    onClick={() => navigate('/contrats-expires')}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Contrats Expirés
                </button>
            </div>

            {/* Alert de sélection */}
            {contratsSelectionnes.length > 0 && (
                <div className="alert alert-info mb-6">
                    <div className="flex-1">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <label>{contratsSelectionnes.length} contrat(s) sélectionné(s) - {getNomsContratsSelectionnes()}</label>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
                {/* Colonne principale - Tableau des contrats */}
                <div className="xl:col-span-3">
                    <div className="card bg-base-100 shadow-xl">
                        <div className="card-body">
                            {/* En-tête avec statistiques */}
                            <div className="flex justify-between items-center mb-6">
                                <div>
                                    <h2 className="card-title text-2xl">Contrats arrivant à échéance</h2>
                                    <p className="text-base-content/70">
                                        {loading ? 'Chargement...' : rows.length} contrat(s) vont se terminer dans les 30 prochains jours
                                    </p>
                                </div>
                                <div className="stats shadow">
                                    <div className="stat">
                                        <div className="stat-title">Total</div>
                                        <div className="stat-value text-warning">{loading ? '...' : rows.length}</div>
                                        <div className="stat-desc">Contrats</div>
                                    </div>
                                </div>
                            </div>

                            {/* Tableau des fins de contrat */}
                            <div className="overflow-x-auto">
                                <table className="table table-zebra w-full">
                                    <thead>
                                        <tr>
                                            <th>
                                                <label className="cursor-pointer">
                                                    <input 
                                                        type="checkbox" 
                                                        className="checkbox checkbox-sm"
                                                        checked={!loading && contratsSelectionnes.length === rows.length && rows.length > 0}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setContratsSelectionnes(rows.map(c => c.id));
                                                            } else {
                                                                setContratsSelectionnes([]);
                                                            }
                                                        }}
                                                    />
                                                </label>
                                            </th>
                                            <th>Employé</th>
                                            <th>Poste</th>
                                            <th>Département</th>
                                            <th>Date de début</th>
                                            <th>Date de fin</th>
                                            <th>Jours restants</th>
                                            <th>Statut</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading && (
                                            <tr>
                                                <td colSpan={8} className="text-center py-8">
                                                    <span className="loading loading-spinner loading-lg text-warning"></span>
                                                    <p className="mt-2 text-base-content/70">Chargement des contrats...</p>
                                                </td>
                                            </tr>
                                        )}
                                        {!loading && error && (
                                            <tr>
                                                <td colSpan={8} className="text-center py-8 text-error">
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.398 16c-.77 1.333.192 3 1.732 3z" />
                                                    </svg>
                                                    <p>{error}</p>
                                                </td>
                                            </tr>
                                        )}
                                        {!loading && !error && rows.length === 0 && (
                                            <tr>
                                                <td colSpan={8} className="text-center py-8 text-base-content/50">
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                    </svg>
                                                    <p>Aucun contrat n'expire dans les 30 prochains jours</p>
                                                </td>
                                            </tr>
                                        )}
                                        {!loading && !error && rows.map((contrat) => {
                                            const joursRestants = calculerJoursRestants(contrat.dateFin);
                                            // Logique standard rétablie :
                                            // <= 7 jours : Urgent (Rouge)
                                            // <= 15 jours : Attention (Jaune)
                                            // > 15 jours : Normal (Vert)
                                            const isUrgent = joursRestants <= 7;
                                            const isWarning = joursRestants <= 15 && joursRestants > 7;
                                            
                                            return (
                                                <tr key={contrat.id} className="hover">
                                                    <td>
                                                        <label className="cursor-pointer">
                                                            <input 
                                                                type="checkbox" 
                                                                className="checkbox checkbox-sm"
                                                                checked={contratsSelectionnes.includes(contrat.id)}
                                                                onChange={() => toggleSelectionContrat(contrat.id)}
                                                            />
                                                        </label>
                                                    </td>
                                                    <td>
                                                        <div className="flex items-center gap-3">
                                                            <div className="avatar">
                                                                <div className="w-10 h-10 rounded-full">
                                                                    {contrat.photo ? (
                                                                        <img
                                                                            src={contrat.photo}
                                                                            alt={contrat.nom}
                                                                            className="w-full h-full object-cover"
                                                                        />
                                                                    ) : (
                                                                        <div className="bg-neutral text-neutral-content rounded-full w-10 h-10 flex items-center justify-center">
                                                                            <span className="text-sm">
                                                                                {contrat.nom.split(' ').map(n => n[0]).join('')}
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div>
                                                                <button
                                                                    type="button"
                                                                    className="font-bold text-left link link-primary no-underline"
                                                                    onClick={() => {
                                                                        const targetId = contrat.employee_pk || contrat.id;
                                                                        navigate(`/employees/${targetId}`);
                                                                    }}
                                                                >
                                                                    {contrat.nom}
                                                                </button>
                                                                <div className="text-sm text-base-content/50">{contrat.email}</div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="font-medium">{contrat.poste}</td>
                                                    <td>{contrat.departement}</td>
                                                    <td>
                                                        <span className="font-medium">
                                                            {contrat.dateDebut ? formaterDate(contrat.dateDebut) : '—'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <div className="flex flex-col">
                                                            <span className="font-medium">{formaterDate(contrat.dateFin)}</span>
                                                            <span className="text-xs text-base-content/50">
                                                                {contrat.dateFin}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span className={`font-bold ${
                                                            isUrgent ? 'text-error' : 
                                                            isWarning ? 'text-warning' : 
                                                            'text-success'
                                                        }`}>
                                                            {joursRestants} jour(s)
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <div className={`badge ${
                                                            isUrgent ? 'badge-error' : 
                                                            isWarning ? 'badge-warning' : 
                                                            'badge-success'
                                                        } gap-1`}>
                                                            {isUrgent ? 'Urgent' : 
                                                             isWarning ? 'À surveiller' : 
                                                             'Normal'}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {/* Actions */}
                            <div className="card-actions justify-between mt-6">
                                <div className="flex gap-2">
                                    <button 
                                        className="btn btn-primary"
                                        onClick={ouvrirModalRappel}
                                        disabled={contratsSelectionnes.length === 0}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                        </svg>
                                        Planifier un rappel ({contratsSelectionnes.length})
                                    </button>
                                </div>
                                <button
                                    className="btn btn-outline"
                                    onClick={exporterListeCSV}
                                    disabled={rows.length === 0}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    Exporter la liste
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Colonne latérale - Rappels planifiés */}
                <div className="xl:col-span-1">
                    <div className="card bg-base-100 shadow-xl h-fit">
                        <div className="card-body">
                            <h3 className="card-title text-lg">📅 Rappels Planifiés</h3>
                            
                            {rappelPlanifies.length === 0 ? (
                                <div className="text-center py-8 text-base-content/50">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                    <p>Aucun rappel planifié</p>
                                    <p className="text-sm">Sélectionnez des contrats et planifiez un rappel</p>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {rappelPlanifies.map((rappel) => (
                                        <div key={rappel.id} className="bg-base-200 rounded-lg p-4">
                                            <div className="flex justify-between items-start mb-2">
                                                <div className="font-semibold">
                                                    Rappel #{rappel.id}
                                                </div>
                                                <button
                                                    onClick={() => supprimerRappel(rappel.id)}
                                                    className="btn btn-ghost btn-xs text-error"
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                            <div className="text-sm space-y-1">
                                                <div>📅 {rappel.dateRappel} à {rappel.heure}</div>
                                                <div>🔄 {rappel.frequence === 'ponctuel' ? 'Ponctuel' : rappel.frequence === 'quotidien' ? 'Quotidien' : 'Hebdomadaire'}</div>
                                                <div>📧 {rappel.methode.includes('email') ? 'Email + ' : ''}Notification</div>
                                                <div className="text-xs text-base-content/50 mt-2">
                                                    {rappel.message}
                                                </div>
                                            </div>
                                            <div className={`badge badge-sm mt-2 ${
                                                rappel.statut === 'actif' ? 'badge-success' : 'badge-ghost'
                                            }`}>
                                                {rappel.statut}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Cartes informatives */}
                    <div className="space-y-4 mt-6">
                        <div className="card bg-base-100 shadow">
                            <div className="card-body">
                                <h3 className="card-title text-lg">📋 Actions recommandées</h3>
                                <ul className="text-sm space-y-2 mt-2">
                                    <li>• Contacter les employés concernés</li>
                                    <li>• Préparer les documents de fin de contrat</li>
                                    <li>• Planifier les entretiens de sortie</li>
                                    <li>• Mettre à jour les plannings</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal de planification de rappel */}
            {modalRappelOpen && (
                <div className="modal modal-open">
                    <div className="modal-box max-w-2xl">
                        <h3 className="font-bold text-lg mb-4">📅 Planifier un Rappel</h3>
                        
                        <div className="space-y-4">
                            {/* Contrats sélectionnés */}
                            <div>
                                <label className="label">
                                    <span className="label-text font-semibold">Contrats sélectionnés</span>
                                </label>
                                <div className="bg-base-200 rounded-lg p-3">
                                    {contratsSelectionnes.map(id => {
                                        const contrat = rows.find(c => c.id === id);
                                        return contrat ? (
                                            <div key={id} className="badge badge-primary badge-lg m-1">
                                                {contrat.nom}
                                            </div>
                                        ) : null;
                                    })}
                                </div>
                            </div>

                            {/* Date et heure */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="label">
                                        <span className="label-text">Date du rappel</span>
                                    </label>
                                    <input 
                                        type="date"
                                        value={nouveauRappel.dateRappel}
                                        onChange={(e) => setNouveauRappel(prev => ({...prev, dateRappel: e.target.value}))}
                                        className="input input-bordered w-full"
                                    />
                                </div>
                                <div>
                                    <label className="label">
                                        <span className="label-text">Heure</span>
                                    </label>
                                    <input 
                                        type="time"
                                        value={nouveauRappel.heure}
                                        onChange={(e) => setNouveauRappel(prev => ({...prev, heure: e.target.value}))}
                                        className="input input-bordered w-full"
                                    />
                                </div>
                            </div>

                            {/* Fréquence */}
                            <div>
                                <label className="label">
                                    <span className="label-text">Fréquence</span>
                                </label>
                                <div className="flex gap-2">
                                    {(['ponctuel', 'quotidien', 'hebdomadaire'] as const).map(freq => (
                                        <button
                                            key={freq}
                                            className={`btn btn-sm ${nouveauRappel.frequence === freq ? 'btn-primary' : 'btn-ghost'}`}
                                            onClick={() => setNouveauRappel(prev => ({...prev, frequence: freq}))}
                                        >
                                            {freq === 'ponctuel' ? '🕐 Ponctuel' : 
                                             freq === 'quotidien' ? '📅 Quotidien' : '🔄 Hebdomadaire'}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Méthode de notification */}
                            <div>
                                <label className="label">
                                    <span className="label-text">Méthode de notification</span>
                                </label>
                                <div className="space-y-2">
                                    <label className="cursor-pointer flex items-center gap-2">
                                        <input 
                                            type="checkbox"
                                            checked={nouveauRappel.methode.includes('email')}
                                            onChange={(e) => {
                                                const newMethode = e.target.checked 
                                                    ? [...nouveauRappel.methode, 'email' as const]
                                                    : nouveauRappel.methode.filter(m => m !== 'email');
                                                setNouveauRappel(prev => ({...prev, methode: newMethode}));
                                            }}
                                            className="checkbox checkbox-sm"
                                        />
                                        <span>📧 Envoyer un email</span>
                                    </label>
                                    <label className="cursor-pointer flex items-center gap-2">
                                        <input 
                                            type="checkbox"
                                            checked={nouveauRappel.methode.includes('notification')}
                                            onChange={(e) => {
                                                const newMethode = e.target.checked 
                                                    ? [...nouveauRappel.methode, 'notification' as const]
                                                    : nouveauRappel.methode.filter(m => m !== 'notification');
                                                setNouveauRappel(prev => ({...prev, methode: newMethode}));
                                            }}
                                            className="checkbox checkbox-sm"
                                        />
                                        <span>🔔 Notification dans l'application</span>
                                    </label>
                                </div>
                            </div>

                            {/* Message personnalisé */}
                            <div>
                                <label className="label">
                                    <span className="label-text">Message personnalisé (optionnel)</span>
                                </label>
                                <textarea 
                                    value={nouveauRappel.message}
                                    onChange={(e) => setNouveauRappel(prev => ({...prev, message: e.target.value}))}
                                    className="textarea textarea-bordered w-full"
                                    rows={3}
                                    placeholder="Message personnalisé pour le rappel..."
                                />
                            </div>
                        </div>

                        <div className="modal-action">
                            <button 
                                className="btn btn-ghost"
                                onClick={() => setModalRappelOpen(false)}
                            >
                                Annuler
                            </button>
                            <button 
                                className="btn btn-primary"
                                onClick={planifierRappel}
                            >
                                📅 Planifier le rappel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FinContrat;